import { appPaths } from '@/app/router/paths.js';
import type { CommandPaletteItem } from '@/app/surfaces/command-palette-types.js';
import { quickPanelRegistry } from '@/app/surfaces/quick-panel-registry.js';
import { isDesignSystemPreviewEnabled } from '@/shared/config/design-system-preview.js';

/** Base navigation and surface commands — extend via feature registries later. */
export function getCommandPaletteItems(): CommandPaletteItem[] {
  const quickPanelItems: CommandPaletteItem[] = Object.entries(quickPanelRegistry).map(
    ([key, entry]) => {
      const item: CommandPaletteItem = {
        id: `quick-panel:${key}`,
        label: `Abrir ${entry.title}`,
        section: 'Painéis rápidos',
        keywords: [entry.title.toLowerCase(), key, 'painel', 'quick panel'],
        run: ({ openQuickPanel, closeCommandPalette }) => {
          openQuickPanel(key);
          closeCommandPalette();
        },
      };

      if (entry.shortcut !== undefined) {
        item.shortcutLabel = `Alt+${entry.shortcut}`;
      }

      return item;
    },
  );

  return [
    {
      id: 'nav-home',
      label: 'Ir para Início',
      section: 'Navegação',
      keywords: ['home', 'inicio', 'painel', 'dashboard'],
      run: ({ navigate, closeCommandPalette }) => {
        void navigate(appPaths.home);
        closeCommandPalette();
      },
    },
    ...(isDesignSystemPreviewEnabled()
      ? [
          {
            id: 'nav-design-system',
            label: 'Abrir design system',
            section: 'Navegação',
            keywords: ['design', 'system', 'tokens', 'showcase', 'preview'],
            run: ({ navigate, closeCommandPalette }) => {
              void navigate(appPaths.harnessDesignSystem);
              closeCommandPalette();
            },
          } satisfies CommandPaletteItem,
        ]
      : []),
    {
      id: 'auth-sign-out',
      label: 'Sair da conta',
      section: 'Conta',
      keywords: ['logout', 'sair', 'encerrar', 'sessao'],
      run: ({ signOut, closeCommandPalette }) => {
        signOut();
        closeCommandPalette();
      },
    },
    ...quickPanelItems,
  ];
}

export function filterCommandPaletteItems(
  items: CommandPaletteItem[],
  query: string,
): CommandPaletteItem[] {
  const normalizedQuery = query.trim().toLowerCase();

  if (!normalizedQuery) {
    return items;
  }

  return items.filter((item) => {
    if (item.label.toLowerCase().includes(normalizedQuery)) {
      return true;
    }

    return item.keywords?.some((keyword) => keyword.includes(normalizedQuery)) ?? false;
  });
}
